import Axios from 'axios';
import { logError, logInfo } from '../services';
let buildConfig = require('../buildSettings');

let CANCEL_TOKEN_SOURCE = Axios.CancelToken.source();

export const generateNewCancelTokenSource = () => {
  CANCEL_TOKEN_SOURCE = Axios.CancelToken.source();
};

export const getCurrentNavigationArea = (path): string => {
  if (path === undefined) return '';
  // If the path is just "/" then return "Global" for now
  if (path === '/' || path === '' || path === '/news') return 'Global';
  // Remove the leading slash and split the path into segments
  const parts = path.replace(/^\//, '').split('/');
  // Filter out any segments that are completely numeric
  let filteredParts = parts.filter(part => !/^\d+$/.test(part));
  // If "contract-management" is in the URL, only take segments up to that one.
  const cmIndex = filteredParts.findIndex(part => part.toLowerCase() === 'contract-management');
  if (cmIndex !== -1) {
    filteredParts = filteredParts.slice(0, cmIndex + 1);
  }
  // If "dynamic" is in the URL, only take segments up to that one.
  const dynamicIndex = filteredParts.findIndex(part => part.toLowerCase() === 'dynamic');
  if (dynamicIndex !== -1) {
    filteredParts = filteredParts.slice(0, dynamicIndex + 1);
  }
  // If "vendors/payments" is in the URL, only take segments up to "payments"
  const vpIndex = filteredParts.findIndex((part, index, arr) => {
    return part.toLowerCase() === 'payments' && arr[index - 1]?.toLowerCase() === 'vendors';
  });
  if (vpIndex !== -1) {
    filteredParts = filteredParts.slice(0, vpIndex + 1);
  }
  // only take segments up to "custom-pages" if it's in the URL when editing a custom page
  // i.e. /admin/manage-content/custom-pages/ob-new-1 -> Admin/Manage Content/Custom Pages
  // check only on the admin edit side not the view side
  const customPageIndex = filteredParts.findIndex(part => part.toLowerCase() === 'custom-pages');
  if (customPageIndex !== -1 && filteredParts.length > 2) {
    filteredParts = filteredParts.slice(0, customPageIndex + 1);
  }

  // Convert each remaining part from kebab-case to Title Case (e.g. "build-selected-options" => "Build Selected Options")
  const titleCaseParts = filteredParts.map(part =>
    part
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  );
  // Join the segments with a slash separator
  return titleCaseParts.join('/');
};

const axiosInstance = Axios.create({
  baseURL: `${buildConfig.REACT_APP_API_URL}`,
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json'
  }
});

// Add the Navigation header on every request.
axiosInstance.interceptors.request.use(config => {
  logInfo(null, `axios request started: ${config.url}`);
  config.cancelToken = CANCEL_TOKEN_SOURCE.token;
  config.headers['Navigation'] = getCurrentNavigationArea(window?.location?.pathname);
  return config;
});

// handle cancelation of requests
axiosInstance.interceptors.response.use(
  response => response,
  error => {
    // if the error is a cancel then don't return any reject errors, but just resolve the promise
    if (Axios.isCancel(error)) {
      logError(error, 'axios response canceled');
      return new Promise(() => {});
    }
    logError(error, 'axios response failed');
    return Promise.reject(error);
  }
);

export const finishPendingRequests = (cancellationReason: string) => {
  CANCEL_TOKEN_SOURCE.cancel(cancellationReason);
  generateNewCancelTokenSource();
};

export default axiosInstance;
